import { RoutePath } from '@constants/routes';

const REDACTED = 'redacted';
const SECRET_QUERY_PARAMS = new Set(
    ['token', 'admin', 'impersonation', 'code', 'state', 'access_token', 'idToken', 'sessionId'].map(param =>
        param.toLowerCase(),
    ),
);
const SECRET_PATH_PREFIXES = [RoutePath.VerifyEmail, RoutePath.ResetPassword, RoutePath.AcceptInvitation].map(prefix =>
    prefix.toLowerCase(),
);
const URL_PROPERTY = /(url|pathname|referrer)$/i;
const NESTED_URL = /^(\/|https?:\/\/)/i;
const URL_PARTS = /^([a-z][a-z\d+.-]*:\/\/[^/?#]*)?([^?#]*)(?:\?([^#]*))?(?:#(.*))?$/is;
const HEATMAP_DATA = '$heatmap_data';

type PropertyBag = Record<string, unknown> | undefined;

function decode(value: string) {
    try {
        return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
        return value;
    }
}

function redactParams(params: string) {
    return params
        .split('&')
        .map(pair => {
            const separator = pair.indexOf('=');
            if (separator < 0) return pair;
            const key = pair.slice(0, separator);
            if (SECRET_QUERY_PARAMS.has(decode(key).toLowerCase())) return `${key}=${REDACTED}`;
            const nested = decode(pair.slice(separator + 1));
            if (!NESTED_URL.test(nested)) return pair;
            const redacted = redactUrl(nested);
            return redacted === nested ? pair : `${key}=${encodeURIComponent(redacted)}`;
        })
        .join('&');
}

function redactPath(path: string) {
    const lowerPath = path.toLowerCase();
    const prefix = SECRET_PATH_PREFIXES.find(secretPrefix => lowerPath.startsWith(`${secretPrefix}/`));
    return prefix ? `${path.slice(0, prefix.length)}/${REDACTED}` : path;
}

export function redactUrl(value: string): string {
    const parts = URL_PARTS.exec(value);
    if (!parts) return value;
    const [, origin = '', path, query, hash] = parts;
    const redactedQuery = query === undefined ? '' : `?${redactParams(query)}`;
    const redactedHash = hash === undefined ? '' : `#${hash.includes('=') ? redactParams(hash) : hash}`;
    return `${origin}${redactPath(path)}${redactedQuery}${redactedHash}`;
}

function redactUrlProperties(bag: PropertyBag) {
    if (!bag) return;
    Object.keys(bag)
        .filter(key => URL_PROPERTY.test(key) && typeof bag[key] === 'string')
        .forEach(key => {
            bag[key] = redactUrl(bag[key] as string);
        });

    const heatmap = bag[HEATMAP_DATA];
    if (heatmap && typeof heatmap === 'object') {
        bag[HEATMAP_DATA] = Object.entries(heatmap).reduce<Record<string, unknown[]>>((redacted, [url, points]) => {
            const key = redactUrl(url);
            redacted[key] = [...(redacted[key] ?? []), ...(Array.isArray(points) ? points : [points])];
            return redacted;
        }, {});
    }
}

export function redactEventUrls<T extends { properties?: PropertyBag; $set?: PropertyBag; $set_once?: PropertyBag }>(
    event: T | null,
): T | null {
    if (event) {
        const { properties } = event;
        [
            properties,
            properties?.$set as PropertyBag,
            properties?.$set_once as PropertyBag,
            event.$set,
            event.$set_once,
        ].forEach(redactUrlProperties);
    }
    return event;
}
